import React, {
  useContext,
  createContext,
  ReactNode,
  useReducer,
  useEffect,
  useMemo,
} from 'react';
import moment from 'moment';
import { error } from '@mummssoftware/utils/constants';
import {
  VisitsGlobalContext,
  SET_CROSSPATIENT_VISITS,
} from '@mummssoftware/global-context';
import {
  SET_SUMMARY_GRID_API,
  SET_API_ERROR,
  SET_SUMMARY_LOADING,
} from './constants';
import { fetchPimreaderVisitsCrossPatient } from './visitSummaryUtils';
import { AppContext } from '../../context';
import defaults from '../../translations/en.json';

type VisitSummaryContextProviderProps = {
  children: ReactNode;
};

type VisitSummaryContext = {
  gridAPI: null | any;
  loading: boolean;
  APIError: any;
  dispatch?: any;
};

type VisitSummaryContextState = VisitSummaryContext;

const initVisitSummaryContext = {
  loading: true,
  APIError: null,
  gridAPI: null,
};

const initialState = initVisitSummaryContext;

export const VisitSummaryContextReducer = (
  state: VisitSummaryContextState,
  action: { type: string; payload: mumms.ComponentName | any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_API_ERROR:
      newState.APIError = action.payload;
      break;
    case SET_SUMMARY_GRID_API:
      newState.gridAPI = action.payload;
      break;
    case SET_SUMMARY_LOADING:
      newState.loading = action.payload;
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const VisitSummaryContext = createContext<VisitSummaryContext>(
  initVisitSummaryContext as VisitSummaryContext,
);
export const VisitSummaryContextProvider = ({
  children,
}: VisitSummaryContextProviderProps) => {
  const [{ loading, gridAPI, APIError }, dispatch] = useReducer(
    VisitSummaryContextReducer,
    initialState,
  );

  const { keycloak, agency, patient, smallWidget, siteId } = useContext(
    AppContext,
  );

  const {
    dispatchVG,
    crossPatientVisitSummary,
    fromDate,
    toDate,
    officeFilter,
    minDurationMM,
    minDurationHH,
    chartAfterDD,
    chartAfterHH,
    summaryAttributeIds,
    summaryAttributeMatchMode,
  } = useContext(VisitsGlobalContext);

  const getInitData = async () => {
    if (!patient) {
      const visits = await fetchPimreaderVisitsCrossPatient(
        keycloak,
        agency,
        siteId,
        fromDate || moment().subtract(30, 'days'),
        toDate || moment(),
        minDurationHH,
        minDurationMM,
        chartAfterDD,
        chartAfterHH,
        summaryAttributeIds,
        summaryAttributeMatchMode,
      );

      if (visits instanceof Response) {
        dispatch({
          type: SET_API_ERROR,
          payload: {
            text: defaults.pimreaderFailed,
            severity: error,
          },
        });
      } else {
        dispatchVG({
          type: SET_CROSSPATIENT_VISITS,
          payload: visits,
        });
      }
    }
  };

  useEffect(() => {
    if (!crossPatientVisitSummary && smallWidget) getInitData();
  }, [crossPatientVisitSummary, smallWidget]);

  useEffect(() => {
    if (!patient && !smallWidget) {
      getInitData();
    }
  }, [
    fromDate,
    toDate,
    officeFilter,
    minDurationHH,
    minDurationMM,
    chartAfterDD,
    chartAfterHH,
    summaryAttributeIds,
    summaryAttributeMatchMode,
  ]);

  const context = useMemo(
    () => ({
      APIError,
      loading,
      gridAPI,
      dispatch,
    }),
    [loading, APIError, gridAPI, dispatch],
  );

  return (
    <VisitSummaryContext.Provider value={context}>
      {children}
    </VisitSummaryContext.Provider>
  );
};

export default VisitSummaryContextProvider;
